import { collection, doc, getDocFromServer, getDocs, query, where } from 'firebase/firestore';
import React, { useContext, useEffect, useRef, useState } from 'react'
import { db } from '../../firebase-config';
import { Button, Container, Loader, Text, Modal, TextInput, Group } from '@mantine/core';
import { Helmet } from 'react-helmet';
import { TestUsersTable } from '../../components/usersTable/TestUsersTable';
import styles from './testUsers.module.css';
import { AppContext } from '../../App';

const chunkArray = (items, size) => {
    const chunks = [];
    for (let index = 0; index < items.length; index += size) {
        chunks.push(items.slice(index, index + size));
    }

    return chunks;
};

const normalizePhone = (value) => {
    return String(value || '').replace(/[\s-]/g, '').trim();
};

const phoneVariants = (value) => {
    const trimmed = String(value || '').trim();
    const noSpaces = trimmed.replace(/\s+/g, '');
    const variants = new Set([trimmed, noSpaces]);

    if (/^\+977\d+$/.test(noSpaces)) {
        variants.add(`+977 ${noSpaces.slice(4)}`);
    }

    if (/^9\d{7,15}$/.test(noSpaces)) {
        variants.add(`+977 ${noSpaces}`);
        variants.add(`+977${noSpaces}`);
    }

    return Array.from(variants).filter(Boolean);
};

const getPersonName = (person) => {
    return person.name || person.displayName || person.fullName || person.email || person.phone || '';
};

export const TestUsers = () => {
    const [latestUsers, setLatestUsers] = useState([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState('');
    // Dialog state
    const [openDialog, setOpenDialog] = useState(false);
    const [newPhone, setNewPhone] = useState('');
    const [newOtp, setNewOtp] = useState('');
    const [saving, setSaving] = useState(false);

    const { globalState } = useContext(AppContext);

    useEffect(() => {
        setLatestUsers([]);
        fetchUsers().then(({ users }) => {
            setLatestUsers(users);
        }).catch((err) => {
            setError('Failed to fetch users: ' + err.message);
        }).finally(() => {
            setLoading(false);
        });
    }, [globalState.currentBrand]);

    // Fetch users based on current brand filter
    const fetchUsers = async () => {
        return await fetchAllUsers();
    };

    // Fetch all users without brand filter
    const fetchAllUsers = async () => {
        const usersQuery = query(collection(db, 'config', 'testUsers', 'users'))

        const querySnapshot = await getDocs(usersQuery);
        const docsToUse = querySnapshot.docs;

        const users = docsToUse.map(doc => ({ ...doc.data(), id: doc.id }));
        const userIds = users.map(user => user.id);
        const matchMap = await fetchPersonMatches(userIds);
        const enrichedUsers = users.map((user) => {
            const match = matchMap.get(user.id);

            return {
                ...user,
                personName: match?.name || '',
                personType: match?.type || ''
            };
        });

        return { users: enrichedUsers, userIds };
    };

    const fetchCollectionMatches = async (collectionName, ids, type) => {
        const matches = new Map();
        const variants = Array.from(new Set(ids.flatMap(phoneVariants)));

        await Promise.all(ids.map(async (id) => {
            try {
                const snapshot = await getDocFromServer(doc(db, collectionName, id));
                if (snapshot.exists()) {
                    const data = snapshot.data();
                    matches.set(id, {
                        name: getPersonName(data),
                        phone: data.phone || id,
                        type
                    });
                }
            } catch (err) {
                // Some staff records may not be readable by the signed-in admin role.
            }
        }));

        await Promise.all(chunkArray(variants, 10).map(async (variantChunk) => {
            try {
                const snapshot = await getDocs(query(
                    collection(db, collectionName),
                    where('phone', 'in', variantChunk)
                ));

                snapshot.docs.forEach((personDoc) => {
                    const data = personDoc.data();
                    const normalizedPhone = normalizePhone(data.phone || personDoc.id);

                    ids.forEach((id) => {
                        if (normalizePhone(id) === normalizedPhone && !matches.has(id)) {
                            matches.set(id, {
                                name: getPersonName(data),
                                phone: data.phone || id,
                                type
                            });
                        }
                    });
                });
            } catch (err) {
                // Ignore unavailable match sources and keep displaying the test user.
            }
        }));

        return matches;
    };

    const fetchPersonMatches = async (ids) => {
        const [userMatches, staffMatches] = await Promise.all([
            fetchCollectionMatches('users', ids, 'User'),
            fetchCollectionMatches('staffs', ids, 'Staff')
        ]);

        const matches = new Map(staffMatches);
        userMatches.forEach((match, id) => {
            matches.set(id, match);
        });

        return matches;
    };

    // Save new test user
    const handleSaveTestUser = async () => {
        if (!/^\+977\s\d{7,15}$/.test(newPhone)) {
            setError('Enter a valid phone number starting with +977 and a space.');
            return;
        }
        if (!/^\d{6}$/.test(newOtp)) {
            setError('OTP must be 6 digits.');
            return;
        }
        setSaving(true);
        setError('');
        try {
            const { doc, setDoc } = await import('firebase/firestore');
            await setDoc(doc(db, 'config', 'testUsers', 'users', newPhone), { code: newOtp });
            const matchMap = await fetchPersonMatches([newPhone]);
            const match = matchMap.get(newPhone);
            setLatestUsers((prev) => [{
                id: newPhone,
                code: newOtp,
                personName: match?.name || '',
                personType: match?.type || ''
            }, ...prev]);
            setOpenDialog(false);
            setNewPhone('');
            setNewOtp('');
        } catch (err) {
            setError('Failed to add test user: ' + err.message);
        } finally {
            setSaving(false);
        }
    };

    return (
        <>
            <Helmet>
                <title>Lollet Admin - Test Users</title>
            </Helmet>
            <Container size="xxl" className={styles.container}>
                <Group mb="md">
                    <Button onClick={() => setOpenDialog(true)}>New Test User</Button>
                </Group>
                {error && <Text color="red" align="center">{error}</Text>}
                {loading ? (
                    <Loader />
                ) : (
                    <Container size="xxl" className={styles.container}>
                        <TestUsersTable data={latestUsers} loading={loading} error={error} />
                    </Container>
                )}
                <Modal opened={openDialog} onClose={() => setOpenDialog(false)} title="Add New Test User" centered>
                    <TextInput
                        label="Phone Number"
                        placeholder="e.g. +9779800000000"
                        value={newPhone}
                        onChange={(e) => setNewPhone(e.target.value)}
                        disabled={saving}
                        maxLength={15}
                        required
                        mb="md"
                    />
                    <TextInput
                        label="OTP"
                        placeholder="6 digit code"
                        value={newOtp}
                        onChange={(e) => setNewOtp(e.target.value.replace(/[^0-9]/g, ''))}
                        maxLength={6}
                        disabled={saving}
                        required
                        mb="md"
                    />
                    <Group position="right">
                        <Button onClick={handleSaveTestUser} loading={saving} disabled={saving} color="blue">Save</Button>
                    </Group>
                </Modal>
            </Container>
        </>
    )
}
